import { type Component, type JSXElement, type ParentComponent, Show } from "solid-js";
import { twMerge } from "tailwind-merge";
import { CloseBtnV2 } from "~/solidJs/entities/SolidModalView/ui/CloseBtn";

/**
 * Props for the Modal component
 */
export type ModalProps = {
	children: JSXElement;
	class?: string;
};

/**
 * Base modal component that renders content in a white rounded container
 * Stops event propagation to prevent clicks from reaching the overlay
 */
export const Modal: Component<ModalProps> = (props) => {
	return (
		<article
			onClick={(e) => e.stopPropagation()}
			class={twMerge(
				"relative flex min-h-[240px] w-[460px] flex-col justify-between overflow-hidden rounded-3xl bg-white",
				props.class
			)}
		>
			{props.children}
		</article>
	);
};

/**
 * Props for the ContentContainerText component
 */
type ContentContainerTextProps = {
	children: JSXElement;
};

/**
 * Container component for text content in modals
 * Provides consistent padding and spacing for text-based content
 */
export const ContentContainerText: Component<ContentContainerTextProps> = (props) => {
	return <div class="flex h-40 flex-col items-start gap-3 px-5 py-4">{props.children}</div>;
};

/**
 * Container component for text content in modals
 * Provides consistent padding and spacing for text-based content
 */
export const ContentContainerTextWithoutPadding: ParentComponent = (props) => {
	return <div class="flex flex-col items-start gap-3">{props.children}</div>;
};

/**
 * Props for the ContentContainerInput component
 */
type ContentContainerInputProps = {
	children: JSXElement;
};

/**
 * Container component for input fields in modals
 * Provides consistent padding and spacing for input-based content
 */
export const ContentContainerInput: Component<ContentContainerInputProps> = (props) => {
	return <div class="flex min-h-40 flex-col items-start gap-5 px-6 py-4">{props.children}</div>;
};

/**
 * Props for the ButtonContainer component
 */
type ButtonContainerProps = {
	children: JSXElement;
	class?: string;
};

/**
 * Container component for modal buttons
 * Renders buttons in a footer section with a top border and consistent spacing
 */
export const ButtonContainer: Component<ButtonContainerProps> = (props) => {
	return (
		<div
			class={twMerge(
				"flex justify-end gap-2 border-border-secondary border-t p-6",
				props.class
			)}
		>
			{props.children}
		</div>
	);
};

/**
 * Props for the ModalOverlay component
 */
type ModalOverlayProps = {
	/** When true, clicking the overlay will close the modal */

	autoClose?: boolean;
	/**
	 * automatic show close button
	 */
	showCloseButton?: boolean;
	/** Optional callback function to close the modal */
	toggleSelf?: () => void;
	/** Content to be rendered inside the overlay */
	children: JSXElement;
};

/**
 * Semi-transparent overlay component that covers the entire viewport
 * Can be configured to close the modal when clicked
 */
export const ModalOverlay: Component<ModalOverlayProps> = (props) => {
	return (
		<div
			class="fixed top-0 left-0 z-[101] flex h-full w-full items-center justify-center bg-[black]/80"
			onClick={() => {
				if (props.autoClose) {
					props.toggleSelf?.();
				}
			}}
		>
			{props.children}
		</div>
	);
};

interface HeaderProps {
	showCloseButton?: boolean;
	toggleSelf?: () => void;
}

export const Header: ParentComponent<HeaderProps> = (props) => {
	return (
		<div class="flex items-start justify-between gap-2 p-6">
			{props.children}
			<Show when={props.showCloseButton}>
				<CloseBtnV2
					variant="SecondaryNeutralIcon"
					type="button"
					onClick={props.toggleSelf}
				/>
			</Show>
		</div>
	);
};

export const HeaderTextContainer: ParentComponent = (props) => {
	return <div class="flex flex-col items-start gap-3">{props.children}</div>;
};

export const HeaderBackButtonContainer: ParentComponent = (props) => {
	return <div class="flex flex-col items-start gap-6">{props.children}</div>;
};

export const ContentContainer: ParentComponent<{ class?: string }> = (props) => {
	return (
		<div class={twMerge("flex items-center justify-between px-6 pb-6", props.class)}>
			{props.children}
		</div>
	);
};

/**
 * Convenience component that combines Modal and ModalOverlay
 * Provides a complete modal solution with overlay and content container
 */
export const ModalWithOverlayBoilerPlate: Component<
	ModalProps & ModalOverlayProps & { scrollContainerClass?: string }
> = (props) => {
	return (
		<ModalOverlay autoClose={props.autoClose} toggleSelf={props.toggleSelf}>
			<div class="relative">
				<Show when={props.showCloseButton}>
					<CloseBtnV2
						type="button"
						class="-right-2 absolute top-0 translate-x-full"
						onClick={props.toggleSelf}
					/>
				</Show>
				<Modal class={props.class}>
					{props.children}
					{/*<div
					class={twMerge(
						"overflow-hidden rounded-3xl flex flex-col",
						props.scrollContainerClass
					)}
				>
					{props.children}
				</div>*/}
				</Modal>
			</div>
		</ModalOverlay>
	);
};
